import update from "immutability-helper";
import moment from "moment-business-days-sk";

import { createSlice } from "@reduxjs/toolkit";
import {
  CaseSearchState,
  OrderSearchState,
  RedoCancelState,
  RedoInvoicePayment,
  RedoInvoiceState,
  RedoPrescription,
  RedoReplaceState,
  RedoScreenState,
  ShipAddress,
  ShipAddressOutcome,
  ShipAddressState,
  ShipCase,
  ShipScreenState,
  UserAddressSaveState,
} from "@types-runway/admin";
import { Case, Order } from "@types-runway/case";
import { Question } from "@types-runway/questionnaire";
import { Partner, PromoCode, Redemption } from "@types-runway/state";
import { Address } from "@types-runway/user";

const partners: Partner[] = [];

const idleCaseSearch: CaseSearchState = { status: "idle", rows: [], total: 0, hasMore: false };

const idleOrderSearch: OrderSearchState = { status: "idle", rows: [] };

const idleShipScreen: ShipScreenState = {
  lookup: "idle",
  shipCase: null,
  orders: [],
  update: { status: "idle" },
};

const idleRedoScreen: RedoScreenState = {
  lookup: "idle",
  shipCase: null,
  orders: [],
  selectedOrderId: null,
  prescriptions: { orderId: null, status: "idle", items: [] },
};

// Records the prescriptions of an Order, unless the screen shows another Order by now (REDOUI-R16).
const withRedoPrescriptions = <State extends { redoScreen: RedoScreenState }>(
  state: State,
  orderId: number,
  outcome: { status: "pending" | "loaded" | "failed"; items: RedoPrescription[] }
): State => {
  const { selectedOrderId } = state.redoScreen;
  if (selectedOrderId !== null && selectedOrderId !== orderId) return state;

  return {
    ...state,
    redoScreen: { ...state.redoScreen, prescriptions: { orderId, ...outcome } },
  };
};

const idleRedoInvoice: RedoInvoiceState = { status: "idle" };

// Records the payment state of an Order only while the screen shows that Order, so that an answer
// arriving during another lookup is dropped (REDOUI-R80).
const withRedoInvoice = <State extends { redoScreen: RedoScreenState }>(
  state: State,
  redoInvoice: RedoInvoiceState & { orderId: number }
): State => {
  if (state.redoScreen.selectedOrderId !== redoInvoice.orderId) return state;

  return { ...state, redoInvoice };
};

const idleRedoCancel: RedoCancelState = { status: "idle" };

const idleRedoReplace: RedoReplaceState = { status: "idle" };

const idleShipAddress: ShipAddressState = {
  dialog: "closed",
  addresses: [],
  step: null,
  failure: null,
  outcome: { status: "none" },
};

const idleUserAddressSave: UserAddressSaveState = { userId: null, status: "idle", failure: null };

// The found user with a saved address listed: in its place when it was edited, last when it
// was created (USER-R27). Another user found meanwhile is left alone.
const withSavedAddress = (users: unknown, userId: string, address: Address) => {
  const user = users as { id?: string; addresses?: Address[] };
  if (user?.id !== userId) return users;

  const addresses = user.addresses ?? [];
  const isKnown = addresses.some((known) => known.id === address.id);

  return {
    ...user,
    addresses: isKnown
      ? addresses.map((known) => (known.id === address.id ? { ...known, ...address } : known))
      : [...addresses, address],
  };
};

const initialState = {
  users: [],
  partners,
  orders: [] as Order[],
  cases: [],
  // The cases of the User the admin looked up (CVIEW-R13); no Case search writes here.
  userCases: [] as Case[],
  transactions: [],
  promoCodes: [],
  redemptions: [],
  credentials: {},
  cancelationError: false,
  caseShippingError: false,
  orderShippingError: false,
  orderSyncError: false,
  lastOrderQuery: null,
  cancellingOrderId: null as string | null,
  deletingPartnerId: null as string | null,
  isUpdatingAddress: false,
  isCaseShippingUpdating: false,
  orderActionsInFlight: {} as Record<string, boolean>,
  isSearchingUsers: false,
  isSearchingCases: false,
  isSearchingOrders: false,
  isSearchingPromoCodes: false,
  userSearchStatus: "idle" as "idle" | "pending" | "found" | "not_found" | "error",
  caseSearch: idleCaseSearch,
  orderSearch: idleOrderSearch,
  shipScreen: idleShipScreen,
  redoScreen: idleRedoScreen,
  redoInvoice: idleRedoInvoice as RedoInvoiceState,
  redoCancel: idleRedoCancel as RedoCancelState,
  redoReplace: idleRedoReplace as RedoReplaceState,
  shipAddress: idleShipAddress,
  userAddressSave: idleUserAddressSave,
  lastCaseSearchParams: null as Record<string, unknown> | null,
};

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    findUser: (state, action) => {
      return (state = { ...state, users: action.payload });
    },
    cancelOrderError: (state, action) => {
      return (state = { ...state, cancelationError: action.payload });
    },
    caseShippingError: (state, action) => {
      return (state = { ...state, caseShippingError: action.payload });
    },
    orderShippingError: (state, action) => {
      return (state = { ...state, orderShippingError: action.payload });
    },
    orderSyncError: (state, action) => {
      return (state = { ...state, orderSyncError: action.payload });
    },
    setLastOrderQuery: (state, action) => ({ ...state, lastOrderQuery: action.payload }),
    setCancellingOrderId: (state, action) => ({ ...state, cancellingOrderId: action.payload }),
    setDeletingPartnerId: (state, action) => ({ ...state, deletingPartnerId: action.payload }),
    setIsUpdatingAddress: (state, action) => ({ ...state, isUpdatingAddress: action.payload }),
    setIsCaseShippingUpdating: (state, action) => ({
      ...state,
      isCaseShippingUpdating: action.payload,
    }),
    setOrderActionInFlight: (
      state,
      action: { payload: { orderId: string; inFlight: boolean } }
    ) => {
      const { orderId, inFlight } = action.payload;
      if (!inFlight) {
        const { [orderId]: _removed, ...rest } = state.orderActionsInFlight;
        return { ...state, orderActionsInFlight: rest };
      }
      return {
        ...state,
        orderActionsInFlight: { ...state.orderActionsInFlight, [orderId]: true },
      };
    },
    setIsSearchingUsers: (state, action) => ({ ...state, isSearchingUsers: action.payload }),
    setUserSearchStatus: (state, action) => ({ ...state, userSearchStatus: action.payload }),
    setIsSearchingCases: (state, action) => ({ ...state, isSearchingCases: action.payload }),
    setIsSearchingOrders: (state, action) => ({ ...state, isSearchingOrders: action.payload }),
    setIsSearchingPromoCodes: (state, action) => ({
      ...state,
      isSearchingPromoCodes: action.payload,
    }),
    setLastCaseSearchParams: (state, action) => ({
      ...state,
      lastCaseSearchParams: action.payload,
    }),
    caseSearchPending: (state) => ({
      ...state,
      caseSearch: { ...idleCaseSearch, status: "pending" },
    }),
    caseSearchFetchSuccess: (
      state,
      action: { payload: { rows: Case[]; total?: number; hasMore?: boolean } }
    ) => {
      const { rows, total = rows.length, hasMore = false } = action.payload;
      return {
        ...state,
        caseSearch: { status: rows.length === 0 ? "empty" : "found", rows, total, hasMore },
      };
    },
    caseSearchError: (state) => ({
      ...state,
      caseSearch: { ...idleCaseSearch, status: "error" },
    }),
    orderSearchPending: (state) => ({
      ...state,
      orderSearch: { ...idleOrderSearch, status: "pending" },
    }),
    orderSearchFetchSuccess: (state, action: { payload: Order[] }) => ({
      ...state,
      orderSearch: {
        status: action.payload.length === 0 ? "empty" : "found",
        rows: action.payload,
      },
    }),
    orderSearchError: (state) => ({
      ...state,
      orderSearch: { ...idleOrderSearch, status: "error" },
    }),
    redoLookupPending: (state) => ({
      ...state,
      redoScreen: { ...idleRedoScreen, lookup: "pending" },
      redoInvoice: idleRedoInvoice,
      redoCancel: idleRedoCancel,
      redoReplace: idleRedoReplace,
    }),
    redoLookupSuccess: (
      state,
      action: { payload: { shipCase: ShipCase; orders: Order[]; selectedOrderId: number } }
    ) => ({
      ...state,
      redoScreen: { ...idleRedoScreen, lookup: "found", ...action.payload },
    }),
    redoLookupNotFound: (state) => ({
      ...state,
      redoScreen: { ...idleRedoScreen, lookup: "not_found" },
    }),
    redoLookupError: (state) => ({
      ...state,
      redoScreen: { ...idleRedoScreen, lookup: "error" },
    }),
    redoOrderSelected: (state, action: { payload: number }) => {
      if (state.redoScreen.selectedOrderId === action.payload) return state;

      return {
        ...state,
        redoScreen: {
          ...state.redoScreen,
          selectedOrderId: action.payload,
          prescriptions: idleRedoScreen.prescriptions,
        },
        redoInvoice: idleRedoInvoice,
        redoCancel: idleRedoCancel,
        redoReplace: idleRedoReplace,
      };
    },
    // The Orders of the Case read again after a replacement, the new Order chosen; the banner of
    // the replacement stays (REDOUI-R36, R38).
    redoOrdersRefreshed: (
      state,
      action: { payload: { shipCase: ShipCase; orders: Order[]; selectedOrderId: number } }
    ) => ({
      ...state,
      redoScreen: {
        ...state.redoScreen,
        lookup: "found" as const,
        ...action.payload,
        prescriptions: idleRedoScreen.prescriptions,
      },
      redoInvoice: idleRedoInvoice,
    }),
    redoReplacePending: (state) => ({
      ...state,
      redoReplace: { status: "pending" as const },
      redoCancel: idleRedoCancel,
    }),
    redoReplaceSucceeded: (
      state,
      action: { payload: { successorOrderId: string; invoiceId: string; caseId: string } }
    ) => ({ ...state, redoReplace: { status: "replaced" as const, ...action.payload } }),
    redoReplaceFailed: (
      state,
      action: { payload: { orderId: string; orderStatus: string; reason: string } }
    ) => ({ ...state, redoReplace: { status: "failed" as const, ...action.payload } }),
    redoReplaceAlreadyDone: (
      state,
      action: {
        payload: Omit<Extract<RedoReplaceState, { status: "alreadyReplaced" }>, "status">;
      }
    ) => ({ ...state, redoReplace: { status: "alreadyReplaced" as const, ...action.payload } }),
    redoReplaceNotSent: (
      state,
      action: {
        payload: {
          successorOrderId: string;
          reason: string;
          prescriptions: RedoPrescription[];
          invoiceId: string;
          caseId: string;
        };
      }
    ) => ({ ...state, redoReplace: { status: "notSent" as const, ...action.payload } }),
    redoReplaceResending: (state) => {
      const { redoReplace } = state;
      if (redoReplace.status === "notSent") {
        return { ...state, redoReplace: { ...redoReplace, status: "resending" as const } };
      }
      if (redoReplace.status === "alreadyReplaced" && redoReplace.resend) {
        return {
          ...state,
          redoReplace: { ...redoReplace, resend: { ...redoReplace.resend, isPending: true } },
        };
      }

      return state;
    },
    redoReplaceCleared: (state) => ({ ...state, redoReplace: idleRedoReplace }),
    redoCancelPending: (state) => ({
      ...state,
      redoCancel: { status: "pending" as const },
      redoReplace: idleRedoReplace,
    }),
    redoCancelSucceeded: (
      state,
      action: { payload: { id: number; orderId: string; wasAtPharmacy: boolean } }
    ) => ({
      ...state,
      redoScreen: {
        ...state.redoScreen,
        orders: state.redoScreen.orders.map((order) =>
          order.id === action.payload.id ? { ...order, status: "cancelled" } : order
        ),
      },
      redoCancel: {
        status: "cancelled" as const,
        orderId: action.payload.orderId,
        wasAtPharmacy: action.payload.wasAtPharmacy,
      },
    }),
    redoCancelRejected: (state, action: { payload: { orderId: string } }) => ({
      ...state,
      redoCancel: { status: "rejected" as const, orderId: action.payload.orderId },
    }),
    redoCancelFailed: (
      state,
      action: { payload: { orderId: string; orderStatus: string; reason: string } }
    ) => ({ ...state, redoCancel: { status: "failed" as const, ...action.payload } }),
    redoPrescriptionsPending: (state, action: { payload: number }) =>
      withRedoPrescriptions(state, action.payload, { status: "pending", items: [] }),
    redoPrescriptionsLoaded: (
      state,
      action: { payload: { orderId: number; items: RedoPrescription[] } }
    ) =>
      withRedoPrescriptions(state, action.payload.orderId, {
        status: "loaded",
        items: action.payload.items,
      }),
    redoPrescriptionsFailed: (state, action: { payload: number }) =>
      withRedoPrescriptions(state, action.payload, { status: "failed", items: [] }),
    redoInvoicePending: (state, action: { payload: number }) =>
      withRedoInvoice(state, { status: "pending", orderId: action.payload }),
    // The provider rides along only when the invoice named one (XCUT-R26).
    redoInvoiceSettled: (
      state,
      action: {
        payload: { orderId: number; payment: RedoInvoicePayment; provider?: string | null };
      }
    ) => {
      const { orderId, payment, provider } = action.payload;

      return withRedoInvoice(state, {
        status: payment,
        orderId,
        ...(provider ? { provider } : {}),
      });
    },
    shipLookupPending: (state) => ({
      ...state,
      shipScreen: { ...idleShipScreen, lookup: "pending" },
      shipAddress: idleShipAddress,
    }),
    shipLookupSuccess: (state, action: { payload: { shipCase: ShipCase; orders: Order[] } }) => ({
      ...state,
      shipScreen: { ...idleShipScreen, lookup: "found", ...action.payload },
      shipAddress: idleShipAddress,
    }),
    shipLookupNotFound: (state) => ({
      ...state,
      shipScreen: { ...idleShipScreen, lookup: "not_found" },
      shipAddress: idleShipAddress,
    }),
    shipLookupError: (state) => ({
      ...state,
      shipScreen: { ...idleShipScreen, lookup: "error" },
      shipAddress: idleShipAddress,
    }),
    // Re-reads the Case and its Orders after a change, leaving the outcome in place.
    shipCaseRefreshed: (state, action: { payload: { shipCase: ShipCase; orders: Order[] } }) => ({
      ...state,
      shipScreen: { ...state.shipScreen, lookup: "found", ...action.payload },
    }),
    shipUpdatePending: (state) => ({
      ...state,
      shipScreen: { ...state.shipScreen, update: { status: "pending" } },
    }),
    shipUpdateCaseOnly: (state, action: { payload: { method: string } }) => ({
      ...state,
      shipScreen: { ...state.shipScreen, update: { status: "caseOnly", ...action.payload } },
    }),
    shipUpdateReplaced: (
      state,
      action: {
        payload: { method: string; predecessorOrderId: string; successorOrderId: string };
      }
    ) => ({
      ...state,
      shipScreen: { ...state.shipScreen, update: { status: "replaced", ...action.payload } },
    }),
    shipUpdateFailed: (state, action: { payload: { reason: string } }) => ({
      ...state,
      shipScreen: { ...state.shipScreen, update: { status: "failed", ...action.payload } },
    }),
    shipUpdateReplacedNotSent: (
      state,
      action: {
        payload: { predecessorOrderId: string; successorOrderId: string; reason: string };
      }
    ) => ({
      ...state,
      shipScreen: {
        ...state.shipScreen,
        update: { status: "replacedNotSent", ...action.payload },
      },
    }),
    shipAddressDialogOpened: (state) => ({
      ...state,
      shipAddress: { ...idleShipAddress, dialog: "loading", outcome: state.shipAddress.outcome },
    }),
    shipAddressesLoaded: (state, action: { payload: { addresses: ShipAddress[] } }) => ({
      ...state,
      shipAddress: { ...state.shipAddress, dialog: "choosing", ...action.payload },
    }),
    shipAddressesFailed: (state) => ({
      ...state,
      shipAddress: { ...state.shipAddress, dialog: "loadFailed" },
    }),
    // A saved or edited address of the User joins the list at once (SHIPUI-R23).
    shipAddressStored: (state, action: { payload: { address: ShipAddress } }) => {
      const stored = action.payload.address;
      const isKnown = state.shipAddress.addresses.some((address) => address.id === stored.id);
      const addresses = isKnown
        ? state.shipAddress.addresses.map((address) =>
            address.id === stored.id ? stored : address
          )
        : [...state.shipAddress.addresses, stored];

      return { ...state, shipAddress: { ...state.shipAddress, addresses, failure: null } };
    },
    shipAddressStep: (state, action: { payload: { step: "saving" | "resending" } }) => ({
      ...state,
      shipAddress: { ...state.shipAddress, dialog: "working", ...action.payload, failure: null },
    }),
    shipAddressFailureCleared: (state) => ({
      ...state,
      shipAddress: { ...state.shipAddress, failure: null },
    }),
    shipAddressFailed: (state, action: { payload: { reason: string } }) => ({
      ...state,
      shipAddress: {
        ...state.shipAddress,
        dialog: "choosing",
        step: null,
        failure: action.payload.reason,
      },
    }),
    shipAddressChanged: (state, action: { payload: { outcome: ShipAddressOutcome } }) => ({
      ...state,
      shipAddress: { ...idleShipAddress, outcome: action.payload.outcome },
    }),
    shipAddressDialogClosed: (state) => ({
      ...state,
      shipAddress: { ...idleShipAddress, outcome: state.shipAddress.outcome },
    }),
    userAddressSavePending: (state, action: { payload: { userId: string } }) => ({
      ...state,
      userAddressSave: { userId: action.payload.userId, status: "saving" as const, failure: null },
    }),
    userAddressSaved: (state, action: { payload: { userId: string; address: Address } }) => {
      const { userId, address } = action.payload;
      const users = withSavedAddress(state.users, userId, address) as typeof state.users;
      if (state.userAddressSave.userId !== userId) return { ...state, users };

      return {
        ...state,
        users,
        userAddressSave: { userId, status: "saved" as const, failure: null },
      };
    },
    userAddressSaveFailed: (state, action: { payload: { userId: string; reason: string } }) => {
      const { userId, reason } = action.payload;
      if (state.userAddressSave.userId !== userId) return state;

      return { ...state, userAddressSave: { userId, status: "failed" as const, failure: reason } };
    },
    userAddressSaveCleared: (state) => ({ ...state, userAddressSave: idleUserAddressSave }),
    shipUpdateReset: (state) => ({
      ...state,
      shipScreen: { ...state.shipScreen, update: { status: "idle" } },
    }),
    getOrders: (state, action) => {
      return (state = { ...state, orders: action.payload });
    },
    // Patches one row of the current order results with the order a mutation
    // returned, so the table reflects the mutation immediately and regardless
    // of how the active search was scoped. Merges rather than replaces: search
    // rows carry a nested `Case` the mutation response does not include.
    updateOrder: (state, action: { payload: Partial<Order> & Pick<Order, "id"> }) => {
      const updatedOrder = action.payload;
      if (updatedOrder?.id === undefined) return state;
      const orders = state.orders.map((order) =>
        order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
      );
      return { ...state, orders };
    },
    userCasesPending: (state) => ({ ...state, userCases: [] }),
    userCasesFetchSuccess: (state, action: { payload: Case[] }) => ({
      ...state,
      userCases: action.payload,
    }),
    getCases: (state, action) => {
      return (state = { ...state, cases: action.payload?.rows });
    },
    getCredentials: (state, action) => {
      return (state = {
        ...state,
        credentials: {
          clientKey: action.payload.clientKey,
          clientSecret: action.payload.decipheredSecret,
        },
      });
    },
    getPartners: (state, action) => {
      return (state = { ...state, partners: action.payload });
    },
    getTransactions: (state, action) => {
      return (state = { ...state, transactions: action.payload });
    },
    removePartner: (state, action) => {
      const partnerIndex = state.partners.findIndex(
        (partner) => partner.clientKey === action.payload
      );
      return update(state, {
        partners: {
          $splice: [[partnerIndex, 1]],
        },
      });
    },
    getPromoCode: (state, action) => {
      const promoCodes = action.payload.map((promoCode: PromoCode) => {
        const firstTime = moment(new Date(promoCode.first_time_redeemed)).format("MM-DD-YYYY");
        const lastTime = moment(new Date(promoCode.last_time_redeemed)).format("MM-DD-YYYY");

        return {
          ...promoCode,
          first_time_redeemed: firstTime,
          last_time_redeemed: lastTime,
        };
      });

      return (state = { ...state, promoCodes });
    },
    getRedemptions: (state, action) => {
      const redemptions = action.payload
        .map((redemption: Redemption) => {
          const createdAt = moment(new Date(redemption.createdAt)).format("MM-DD-YYYY");
          return {
            ...redemption,
            createdAt,
          };
        })
        .sort((a: Redemption, b: Redemption) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });

      return (state = { ...state, redemptions });
    },
  },
});

export const {
  findUser,
  cancelOrderError,
  caseShippingError,
  orderShippingError,
  orderSyncError,
  setLastOrderQuery,
  setCancellingOrderId,
  setDeletingPartnerId,
  setIsUpdatingAddress,
  setIsCaseShippingUpdating,
  setOrderActionInFlight,
  setIsSearchingUsers,
  setUserSearchStatus,
  setIsSearchingCases,
  setIsSearchingOrders,
  setIsSearchingPromoCodes,
  setLastCaseSearchParams,
  caseSearchPending,
  caseSearchFetchSuccess,
  caseSearchError,
  orderSearchPending,
  orderSearchFetchSuccess,
  orderSearchError,
  redoLookupPending,
  redoLookupSuccess,
  redoLookupNotFound,
  redoLookupError,
  redoOrderSelected,
  redoPrescriptionsPending,
  redoPrescriptionsLoaded,
  redoPrescriptionsFailed,
  redoInvoicePending,
  redoInvoiceSettled,
  redoCancelPending,
  redoCancelSucceeded,
  redoCancelRejected,
  redoCancelFailed,
  redoOrdersRefreshed,
  redoReplacePending,
  redoReplaceSucceeded,
  redoReplaceFailed,
  redoReplaceAlreadyDone,
  redoReplaceNotSent,
  redoReplaceResending,
  redoReplaceCleared,
  shipLookupPending,
  shipLookupSuccess,
  shipLookupNotFound,
  shipLookupError,
  shipCaseRefreshed,
  shipUpdatePending,
  shipUpdateCaseOnly,
  shipUpdateReplaced,
  shipUpdateFailed,
  shipUpdateReplacedNotSent,
  shipUpdateReset,
  shipAddressDialogOpened,
  shipAddressesLoaded,
  shipAddressesFailed,
  shipAddressStored,
  shipAddressStep,
  shipAddressFailed,
  shipAddressFailureCleared,
  shipAddressChanged,
  shipAddressDialogClosed,
  userAddressSavePending,
  userAddressSaved,
  userAddressSaveFailed,
  userAddressSaveCleared,
  getOrders,
  updateOrder,
  getCases,
  userCasesPending,
  userCasesFetchSuccess,
  getPartners,
  getCredentials,
  getTransactions,
  removePartner,
  getPromoCode,
  getRedemptions,
} = adminSlice.actions;

export default adminSlice.reducer;
